import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Phone, MessageCircle, MapPin, Clock, Star, ArrowUpRight, Menu, X, Mail, Instagram, Play, ShieldCheck } from "lucide-react";
import { STORE, waLink, telLink } from "@/store";
import { T, tr } from "@/i18n";
import { useLang } from "@/LanguageContext";
import { api, mediaUrl, formatApiError } from "@/api";
import { toast } from "sonner";

const ease = [0.16, 1, 0.3, 1];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 34 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.8, ease, delay }}
  >
    {children}
  </motion.div>
);

const Kicker = ({ children }) => (
  <span className="text-sm uppercase tracking-[0.25em] font-semibold text-primary flex items-center gap-3">
    <span className="w-8 h-px bg-primary inline-block" />
    {children}
  </span>
);

/* ---------------- Header ---------------- */
const Header = () => {
  const { lang, toggle } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const links = ["products", "brands", "about", "gallery", "reviews", "contact"];
  const go = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-40 transition-colors duration-300 ${
        scrolled ? "bg-[#F5F2EC]/90 backdrop-blur-md border-b border-border/60" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
        <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
           data-testid="logo" className="flex items-center gap-3 max-w-[60%]">
          <img src="/logo.jpg" alt="logo" className="h-12 w-12 rounded-full object-cover shrink-0 border border-foreground/10" />
          <span className="font-head font-extrabold text-lg sm:text-xl tracking-tight leading-none">
            {lang === "mr" ? STORE.name_mr : STORE.name_en}
          </span>
        </a>
        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <button key={l} data-testid={`nav-${l}`} onClick={() => go(l)}
                    className="link-underline text-sm font-semibold tracking-wide uppercase">
              {tr(T.nav[l], lang)}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-4">
          <button data-testid="lang-toggle" onClick={toggle} aria-label="Toggle language"
                  className="text-sm font-bold tracking-widest border border-foreground/30 px-3 py-1.5 hover:bg-foreground hover:text-background transition-colors">
            {lang === "en" ? "मराठी" : "ENG"}
          </button>
          <a href={telLink()} data-testid="header-call" aria-label="Call"
             className="hidden sm:inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:bg-foreground transition-colors">
            <Phone size={15} /> {STORE.phoneDisplay}
          </a>
          <button className="lg:hidden" data-testid="menu-toggle" onClick={() => setOpen((o) => !o)} aria-label="Menu">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden bg-[#F5F2EC] border-t border-border/60 px-5 py-4 flex flex-col gap-3">
          {links.map((l) => (
            <button key={l} data-testid={`mnav-${l}`} onClick={() => go(l)}
                    className="text-left text-base font-semibold uppercase tracking-wide py-1">
              {tr(T.nav[l], lang)}
            </button>
          ))}
        </div>
      )}
    </header>
  );
};

/* ---------------- Hero ---------------- */
const Hero = () => {
  const { lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const lines = [T.hero.line1, T.hero.line2, T.hero.line3];
  return (
    <section id="top" ref={ref} className="relative min-h-screen flex items-end overflow-hidden bg-[#1A1A1A]">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src="https://images.pexels.com/photos/4709407/pexels-photo-4709407.jpeg"
             alt="Marble texture" className="img-cover opacity-55" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] via-[#1A1A1A]/40 to-[#1A1A1A]/70" />
      </motion.div>
      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-5 sm:px-8 pb-16 sm:pb-24 pt-32 text-[#F5F2EC]">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}
                    className="mb-6 text-sm uppercase tracking-[0.25em] font-semibold text-accent">
          {tr(T.hero.since, lang)} {STORE.established} · Vairag
        </motion.div>
        <h1 className="font-head font-black leading-[0.92] tracking-tighter text-5xl sm:text-7xl lg:text-[7.5vw] max-w-[15ch]">
          {lines.map((ln, i) => (
            <span key={`${ln.en}-${i}`} className="block overflow-hidden">
              <motion.span className="block"
                initial={{ y: "110%" }} animate={{ y: 0 }}
                transition={{ duration: 0.9, ease, delay: 0.15 + i * 0.13 }}>
                {tr(ln, lang)}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.7 }}
                  className="mt-8 max-w-xl text-base sm:text-lg text-[#F5F2EC]/80 leading-relaxed">
          {tr(T.hero.sub, lang)}
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.85 }}
                    className="mt-10 flex flex-wrap gap-4">
          <a href={telLink()} data-testid="hero-call"
             className="group inline-flex items-center gap-2 bg-primary text-primary-foreground px-7 py-4 text-sm font-bold uppercase tracking-wider hover:bg-accent hover:text-foreground transition-colors">
            <Phone size={17} /> {tr(T.hero.cta_call, lang)}
          </a>
          <a href={waLink()} target="_blank" rel="noreferrer" data-testid="hero-whatsapp"
             className="group inline-flex items-center gap-2 border border-[#F5F2EC]/40 px-7 py-4 text-sm font-bold uppercase tracking-wider hover:bg-[#F5F2EC] hover:text-foreground transition-colors">
            <MessageCircle size={17} /> {tr(T.hero.cta_wa, lang)}
          </a>
          <a href={STORE.instagram} target="_blank" rel="noopener noreferrer" data-testid="hero-instagram"
             className="group inline-flex items-center gap-2 border border-[#F5F2EC]/40 px-7 py-4 text-sm font-bold uppercase tracking-wider hover:bg-[#F5F2EC] hover:text-foreground transition-colors">
            <Instagram size={17} /> {tr(T.hero.cta_ig, lang)}
          </a>
        </motion.div>
      </div>
    </section>
  );
};

/* ---------------- Marquee ---------------- */
const Ribbon = () => {
  const items = ["Tiles", "Granite", "Sanitaryware", "Plumbing", "Waterproofing", "Faucets", "Water Tanks"];
  return (
    <div className="bg-[#1A1A1A] text-[#F5F2EC] py-5 border-y border-[#F5F2EC]/10">
      <Marquee speed={38} gradient={false}>
        {items.concat(items).map((it, i) => (
          <span key={`${it}-${i}`} className="font-serif-accent italic text-2xl sm:text-3xl mx-12 opacity-90">
            {it} <span className="text-accent not-italic mx-2">✦</span>
          </span>
        ))}
      </Marquee>
    </div>
  );
};

/* ---------------- Products ---------------- */
const Products = ({ data }) => {
  const { lang } = useLang();
  return (
    <section id="products" className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24 sm:py-32">
      <Reveal>
        <Kicker>{tr(T.products.kicker, lang)}</Kicker>
        <h2 className="font-head font-extrabold text-4xl sm:text-6xl tracking-tight mt-4">
          {tr(T.products.title, lang)}
        </h2>
      </Reveal>
      <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.map((p, i) => {
          const tall = i % 5 === 0;
          return (
            <Reveal key={p.id} delay={(i % 3) * 0.08}
                    className={tall ? "lg:row-span-2" : ""}>
              <a href={waLink(`Enquiry about ${p.name_en}`)} target="_blank" rel="noreferrer"
                 data-testid={`product-${i}`}
                 className={`group relative block overflow-hidden bg-muted border border-border/60 ${tall ? "h-full min-h-[280px]" : "aspect-[4/5]"}`}>
                <img src={mediaUrl(p.image)} alt={p.name_en} className="img-cover hover-zoom" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                {p.badge_en && (
                  <span data-testid={`product-badge-${i}`}
                        className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-accent text-foreground px-3 py-1.5 text-xs font-black uppercase tracking-wider shadow-lg">
                    <ShieldCheck size={14} /> {lang === "mr" ? p.badge_mr : p.badge_en}
                  </span>
                )}
                <div className="absolute top-4 right-4 w-9 h-9 bg-primary text-primary-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                  <ArrowUpRight size={18} />
                </div>
                <div className="absolute bottom-0 left-0 p-6 text-[#F5F2EC]">
                  <h3 className="font-head font-bold text-xl sm:text-2xl leading-tight">
                    {lang === "mr" ? p.name_mr : p.name_en}
                  </h3>
                  <p className="text-sm text-[#F5F2EC]/75 mt-1 max-w-xs">{lang === "mr" ? p.desc_mr : p.desc_en}</p>
                  <span className="mt-3 inline-block text-xs uppercase tracking-[0.2em] font-semibold text-accent">
                    {tr(T.products.enquire, lang)} →
                  </span>
                </div>
              </a>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
};

/* ---------------- Brands ---------------- */
const Brands = ({ data }) => {
  const { lang } = useLang();
  return (
    <section id="brands" className="bg-[#1A1A1A] text-[#F5F2EC] py-24 sm:py-32">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <Reveal>
          <Kicker>{tr(T.brands.kicker, lang)}</Kicker>
          <h2 className="font-head font-extrabold text-4xl sm:text-6xl tracking-tight mt-4">
            {tr(T.brands.title, lang)}
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 border-t border-l border-[#F5F2EC]/15">
          {data.map((b, i) => {
            const Cell = b.website ? "a" : "div";
            const extra = b.website ? { href: b.website, target: "_blank", rel: "noopener noreferrer" } : {};
            return (
            <Reveal key={b.id} delay={(i % 5) * 0.05}>
              <Cell data-testid={`brand-${i}`} {...extra}
                   className={`border-b border-r border-[#F5F2EC]/15 h-32 flex flex-col items-center justify-center gap-2 px-4 group hover:bg-[#F5F2EC]/5 transition-colors ${b.website ? "cursor-pointer" : ""}`}>
                {b.logo ? (
                  <>
                    <span className="bg-white rounded-md px-3 py-2 flex items-center justify-center">
                      <img src={mediaUrl(b.logo)} alt={b.name} className="h-10 max-w-[140px] object-contain" />
                    </span>
                    <span className="text-xs font-semibold tracking-widest uppercase text-[#F5F2EC]/50 group-hover:text-accent transition-colors inline-flex items-center gap-1.5">
                      {b.name}{b.website && <ArrowUpRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />}
                    </span>
                  </>
                ) : (
                  <span className="font-head font-bold text-xl sm:text-2xl text-[#F5F2EC]/70 group-hover:text-accent transition-colors">
                    {b.name}
                  </span>
                )}
              </Cell>
            </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ---------------- About ---------------- */
const About = () => {
  const { lang } = useLang();
  return (
    <section id="about" className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24 sm:py-32">
      <Reveal>
        <Kicker>{tr(T.about.kicker, lang)}</Kicker>
        <h2 className="font-head font-extrabold text-4xl sm:text-6xl tracking-tight mt-4 max-w-2xl">
          {tr(T.about.title, lang)}
        </h2>
      </Reveal>
      <div className="mt-16 space-y-16">
        {T.about.chapters.map((c, i) => (
          <Reveal key={c.no} delay={0.05}>
            <div className={`grid lg:grid-cols-12 gap-6 items-start ${i % 2 ? "lg:pl-[16%]" : ""}`}>
              <div className="lg:col-span-3">
                <span className="font-serif-accent text-7xl sm:text-8xl text-primary/25 leading-none">{c.no}</span>
              </div>
              <div className="lg:col-span-9 border-t border-border pt-6">
                <h3 className="font-head font-bold text-2xl sm:text-3xl tracking-tight">{tr(c.h, lang)}</h3>
                <p className="mt-4 text-lg text-muted-foreground leading-relaxed max-w-2xl">{tr(c.p, lang)}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

/* ---------------- Gallery ---------------- */
const Gallery = ({ data }) => {
  const { lang } = useLang();
  const [active, setActive] = useState(null);
  return (
    <section id="gallery" className="bg-muted/40 py-24 sm:py-32">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <Reveal>
          <Kicker>{tr(T.gallery.kicker, lang)}</Kicker>
          <h2 className="font-head font-extrabold text-4xl sm:text-6xl tracking-tight mt-4">
            {tr(T.gallery.title, lang)}
          </h2>
        </Reveal>
        <div className="mt-14 columns-1 sm:columns-2 lg:columns-3 gap-4 [column-fill:_balance]">
          {data.map((g, i) => (
            <Reveal key={g.id} delay={(i % 3) * 0.06} className="mb-4 break-inside-avoid">
              <button data-testid={`gallery-${i}`} onClick={() => setActive(g)}
                      className="group relative block w-full overflow-hidden border border-border/60">
                <img src={mediaUrl(g.image)} alt={g.caption_en} className="img-cover hover-zoom" loading="lazy" />
                {g.video && (
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="w-14 h-14 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Play size={22} className="text-white ml-1" fill="currentColor" />
                    </span>
                  </span>
                )}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-end">
                  <span className="p-4 text-[#F5F2EC] text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                    {lang === "mr" ? g.caption_mr : g.caption_en}
                  </span>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </div>
      {active && (
        <div data-testid="gallery-lightbox" onClick={() => setActive(null)}
             className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4">
          <button className="absolute top-6 right-6 text-white" aria-label="Close"><X size={30} /></button>
          <figure className="max-w-4xl" onClick={(e) => active.video && e.stopPropagation()}>
            {active.video ? (
              <video src={mediaUrl(active.video)} poster={mediaUrl(active.image)} controls autoPlay loop muted playsInline
                     data-testid="gallery-video-player" className="max-h-[80vh] w-auto mx-auto" />
            ) : (
              <img src={mediaUrl(active.image)} alt={active.caption_en} className="max-h-[80vh] w-auto mx-auto" />
            )}
            <figcaption className="text-center text-[#F5F2EC]/80 mt-4">
              {lang === "mr" ? active.caption_mr : active.caption_en}
            </figcaption>
          </figure>
        </div>
      )}
    </section>
  );
};

/* ---------------- Reviews ---------------- */
const Stars = ({ n, size = 15 }) => (
  <span className="inline-flex text-accent">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={size} fill={i <= n ? "currentColor" : "none"} className={i <= n ? "" : "text-muted-foreground/40"} />
    ))}
  </span>
);

const Reviews = ({ data, reload }) => {
  const { lang } = useLang();
  const [form, setForm] = useState({ name: "", rating: 5, message: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) return;
    setBusy(true);
    try {
      await api.post("/reviews", form);
      toast.success(tr(T.reviews.thanks, lang));
      setForm({ name: "", rating: 5, message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section id="reviews" className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24 sm:py-32">
      <Reveal>
        <Kicker>{tr(T.reviews.kicker, lang)}</Kicker>
        <h2 className="font-head font-extrabold text-4xl sm:text-6xl tracking-tight mt-4">
          {tr(T.reviews.title, lang)}
        </h2>
      </Reveal>
      <div className="mt-14 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 columns-1 sm:columns-2 gap-4">
          {data.length === 0 && <p className="text-muted-foreground">{tr(T.reviews.empty, lang)}</p>}
          {data.map((r, i) => (
            <Reveal key={r.id || i} className="mb-4 break-inside-avoid">
              <div data-testid={`review-${i}`} className="border border-border/60 p-6 bg-card">
                <Stars n={r.rating} />
                <p className="mt-3 text-base leading-relaxed">"{r.message}"</p>
                <p className="mt-4 font-head font-bold text-sm uppercase tracking-wider">— {r.name}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="lg:col-span-5">
          <Reveal>
            <form onSubmit={submit} data-testid="review-form" className="border border-border p-7 bg-[#1A1A1A] text-[#F5F2EC]">
              <h3 className="font-head font-bold text-2xl mb-6">{tr(T.reviews.write, lang)}</h3>
              <label className="block text-xs uppercase tracking-widest mb-2 text-[#F5F2EC]/70">{tr(T.reviews.name, lang)}</label>
              <input data-testid="review-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     className="w-full bg-transparent border border-[#F5F2EC]/25 px-4 py-3 mb-5 focus:border-accent outline-none" required />
              <label className="block text-xs uppercase tracking-widest mb-2 text-[#F5F2EC]/70">{tr(T.reviews.rating, lang)}</label>
              <div className="flex gap-2 mb-5" data-testid="review-rating">
                {[1, 2, 3, 4, 5].map((i) => (
                  <button type="button" key={i} onClick={() => setForm({ ...form, rating: i })} aria-label={`${i} star`}>
                    <Star size={26} fill={i <= form.rating ? "currentColor" : "none"}
                          className={i <= form.rating ? "text-accent" : "text-[#F5F2EC]/30"} />
                  </button>
                ))}
              </div>
              <label className="block text-xs uppercase tracking-widest mb-2 text-[#F5F2EC]/70">{tr(T.reviews.message, lang)}</label>
              <textarea data-testid="review-message" value={form.message} rows={4}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        className="w-full bg-transparent border border-[#F5F2EC]/25 px-4 py-3 mb-6 focus:border-accent outline-none resize-none" required />
              <button type="submit" disabled={busy} data-testid="review-submit"
                      className="w-full bg-primary text-primary-foreground py-4 font-bold uppercase tracking-wider hover:bg-accent hover:text-foreground transition-colors disabled:opacity-60">
                {busy ? "..." : tr(T.reviews.submit, lang)}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

/* ---------------- Contact ---------------- */
const Contact = () => {
  const { lang } = useLang();
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) return;
    setBusy(true);
    try {
      await api.post("/enquiries", form);
      toast.success(tr(T.contact.sent, lang));
      setForm({ name: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const items = [
    { icon: Phone, label: T.contact.phone, value: STORE.phoneDisplay, href: telLink(), testid: "contact-call" },
    { icon: MessageCircle, label: T.contact.whatsapp, value: STORE.phoneDisplay, href: waLink(), testid: "contact-whatsapp" },
    { icon: Mail, label: T.contact.email, value: STORE.email, href: `mailto:${STORE.email}`, testid: "contact-email" },
    { icon: Instagram, label: T.contact.instagram, value: "@uttam_traders_14", href: STORE.instagram, testid: "contact-instagram" },
    { icon: MapPin, label: T.contact.address, value: lang === "mr" ? STORE.address_mr : STORE.address_en, href: STORE.mapLink, testid: "contact-map" },
    { icon: Clock, label: T.contact.hours, value: lang === "mr" ? STORE.hours_mr : STORE.hours_en, testid: "contact-hours" },
  ];
  return (
    <section id="contact" className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24 sm:py-32">
      <Reveal>
        <Kicker>{tr(T.contact.kicker, lang)}</Kicker>
        <h2 className="font-head font-extrabold text-4xl sm:text-6xl tracking-tight mt-4">
          {tr(T.contact.title, lang)}
        </h2>
      </Reveal>
      <div className="mt-14 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5 space-y-1 border-t border-l border-border">
          {items.map((it, i) => {
            const Inner = (
              <div className="flex gap-4 p-6 border-b border-r border-border group hover:bg-muted/50 transition-colors h-full">
                <it.icon className="text-primary shrink-0 mt-1" size={22} />
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">{tr(it.label, lang)}</div>
                  <div className="font-semibold mt-1">{it.value}</div>
                </div>
              </div>
            );
            return it.href ? (
              <a key={it.testid} data-testid={it.testid} href={it.href} target={it.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="block">{Inner}</a>
            ) : (
              <div key={it.testid} data-testid={it.testid}>{Inner}</div>
            );
          })}
          <a href={STORE.mapLink} target="_blank" rel="noreferrer" data-testid="directions-btn"
             className="flex items-center justify-between p-6 border-b border-r border-border bg-primary text-primary-foreground font-bold uppercase tracking-wider hover:bg-foreground transition-colors">
            {tr(T.contact.directions, lang)} <ArrowUpRight size={20} />
          </a>
        </div>
        <div className="lg:col-span-4">
          <div className="w-full h-64 lg:h-full border border-border overflow-hidden grayscale hover:grayscale-0 transition-all duration-500">
            <iframe title="Map" src={STORE.mapEmbed} className="w-full h-full min-h-[300px]" loading="lazy" style={{ border: 0 }} />
          </div>
        </div>
        <div className="lg:col-span-3">
          <form onSubmit={submit} data-testid="enquiry-form" className="border border-border p-6">
            <h3 className="font-head font-bold text-xl mb-5">{tr(T.contact.form_title, lang)}</h3>
            <input data-testid="enquiry-name" placeholder={tr(T.reviews.name, lang)} value={form.name}
                   onChange={(e) => setForm({ ...form, name: e.target.value })}
                   className="w-full bg-transparent border border-border px-4 py-3 mb-3 focus:border-primary outline-none" required />
            <input data-testid="enquiry-phone" placeholder={tr(T.contact.yourphone, lang)} value={form.phone}
                   onChange={(e) => setForm({ ...form, phone: e.target.value })}
                   className="w-full bg-transparent border border-border px-4 py-3 mb-3 focus:border-primary outline-none" required />
            <textarea data-testid="enquiry-message" rows={3} placeholder={tr(T.reviews.message, lang)} value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      className="w-full bg-transparent border border-border px-4 py-3 mb-4 focus:border-primary outline-none resize-none" />
            <button type="submit" disabled={busy} data-testid="enquiry-submit"
                    className="w-full bg-foreground text-background py-3.5 font-bold uppercase tracking-wider hover:bg-primary transition-colors disabled:opacity-60">
              {busy ? "..." : tr(T.contact.send, lang)}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

/* ---------------- Footer ---------------- */
const Footer = () => {
  const { lang } = useLang();
  return (
    <footer className="bg-[#1A1A1A] text-[#F5F2EC] py-14">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <img src="/logo.jpg" alt="logo" className="h-20 w-20 rounded-full object-cover mb-6" data-testid="footer-logo" />
        <div className="font-head font-black text-3xl sm:text-5xl tracking-tight max-w-3xl">
          {lang === "mr" ? STORE.name_mr : STORE.name_en}
        </div>
        <p className="mt-3 text-[#F5F2EC]/60 max-w-md">{lang === "mr" ? STORE.tagline_mr : STORE.tagline_en}</p>
        <div className="mt-10 flex flex-wrap items-center justify-between gap-4 border-t border-[#F5F2EC]/15 pt-6 text-sm text-[#F5F2EC]/50">
          <span>© {new Date().getFullYear()} {lang === "mr" ? STORE.name_mr : STORE.name_en}. {tr(T.footer.rights, lang)}</span>
          <div className="flex items-center gap-5">
            <a href={STORE.instagram} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram"
               className="link-underline hover:text-[#F5F2EC] inline-flex items-center gap-1.5">
              <Instagram size={14} /> @uttam_traders_14
            </a>
            <a href="/admin/login" data-testid="footer-admin" className="link-underline hover:text-[#F5F2EC]">{tr(T.footer.admin, lang)}</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

/* ---------------- Floating actions ---------------- */
const Floating = () => (
  <div className="fixed bottom-5 right-5 z-40 flex flex-col gap-3">
    <a href={waLink()} target="_blank" rel="noreferrer" data-testid="float-whatsapp" aria-label="WhatsApp"
       className="w-14 h-14 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-lg hover:scale-110 transition-transform">
      <MessageCircle size={26} />
    </a>
    <a href={telLink()} data-testid="float-call" aria-label="Call"
       className="w-14 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg hover:scale-110 transition-transform">
      <Phone size={24} />
    </a>
  </div>
);

/* ---------------- Page ---------------- */
export default function Landing() {
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [reviews, setReviews] = useState([]);

  const loadReviews = () => api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/brands").then((r) => setBrands(r.data)).catch(() => {});
    api.get("/gallery").then((r) => setGallery(r.data)).catch(() => {});
    loadReviews();
  }, []);

  return (
    <div className="grain overflow-x-hidden">
      <Header />
      <Hero />
      <Ribbon />
      <Products data={products} />
      <Brands data={brands} />
      <About />
      <Gallery data={gallery} />
      <Reviews data={reviews} reload={loadReviews} />
      <Contact />
      <Footer />
      <Floating />
    </div>
  );
}
